import { Fragment, useMemo, useRef, useState } from 'react'

import {
    Event,
    LiveButton,
    RealtimeTracker,
    Timeline,
    TimelineBodyContainer,
    TimelineContainer,
    TimelineContainerDevider,
    TimelineEventTrack,
    TimelineHeader,
    TimelineScrollContainer,
    TimelineTrack,
    TimelineTrackContainer
} from '../../../../../CommonComponents/timeline/Timeline'
import useViewer from '../../../../hooks/useViewer';
import useGroundContact from '../../../../hooks/useGroundContact';
import useUrlParams from '../../../../hooks/useUrlParams';
import useScenario from '../../../../hooks/useScenario';
import { isOperateMode } from '../../../../hooks/utils';
import { TimeFrameInMinutes, TimeFrameOptions } from '../../../../../CommonComponents/timeline/constant';
import { ContactTaskSvg, TimeIntervalSvg } from '../../../../../CommonComponents/svgs/Svgs';
import { ContactStation } from '../../../../types';
import { ButtonSvg, event_status_state } from '../../hud/contact/Svgs';
import ContactOverlay from '../../../../../CommonComponents/overlays/ContactOverlay';
import { getNextTimeFrame } from '../../../../../CommonComponents/timeline/utils';
import { useScrollBoundary } from '../../../../../CommonComponents/timeline/hooks/useScrollBoundary';
import { useActivePointOnScroll } from '../../../../../CommonComponents/timeline/hooks/useActivePointProps';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import OmniSelect from '../../../../../CommonComponents/omni-select/OmniSelect';
import useLoader from '../../../../hooks/useLoader';
import useTimeframe from '../../../../hooks/useTimeframe';
import { getFormatedTime } from '../../../../../../utils/commonFunctions';
import { getContactCurrentStatus } from '../../../../utils';


const GroundContactSection = () => {
    const { loading } = useLoader()
    const { dashboard } = useUrlParams()
    const { change_viewer_time, go_live } = useViewer();
    const { scenario, getUpdatedSimulationTime } = useScenario();
    const { configured_gs, planingContactData, selectedContact, setSelectedContact } = useGroundContact()

    const {
        simulation_start_time,
        simulation_end_time,
        simulation_duration,
        current_time,
        showFrameButton,
        getUpdatedFrame,
    } = useTimeframe();

    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const [openSelect, setOpenSelect] = useState<null | string>(null)
    const [selectedOrbit, setSelectedOrbit] = useState('Orbit 1');
    const [selectedFrame, setSelectedFrame] = useState(TimeFrameOptions[0]);
    const operateMode = isOperateMode(dashboard, scenario)

    const frameStartTime = useMemo(() => operateMode ? simulation_start_time : scenario?.scenarioStartTime || 0, [scenario, operateMode, simulation_start_time])
    const frameDuration = useMemo(() => operateMode ? simulation_duration : scenario?.scenarioDuration || 0, [scenario, operateMode, simulation_duration])
    const frameEndTime = useMemo(() => operateMode ? simulation_end_time : (frameStartTime + (frameDuration * 60)), [scenario, operateMode, simulation_end_time, frameStartTime, frameDuration])

    const time = useMemo(() => operateMode ? current_time : simulation_start_time, [operateMode, current_time, simulation_start_time])

    /// This function will be used to change the orbit ///
    const onOrbitChange = (orbit: string) => {
        const orbitIndex = Number(orbit.replace(/\D/g, "") || 1)
        const selectedOrbit = `orbit_${orbitIndex}`
        const orbitPosition = document.getElementById(selectedOrbit)
        if (!scrollAreaRef.current || !orbitPosition) return
        scrollAreaRef.current.scrollLeft = orbitPosition.offsetLeft
        setSelectedOrbit(orbit);
    }

    useActivePointOnScroll({
        scrollAreaRef: scrollAreaRef,
        pointSelector: ".orbit__point",
        selectedFrame: selectedFrame,
        onActiveChange: (visiblePoints) => {
            if (!visiblePoints || visiblePoints.length === 0) return;
            // visiblePoints are sorted by left; first and last define the visible range
            if (visiblePoints.length === 1) {
                const id = visiblePoints[0].id; // e.g. 'orbit_1'
                const numMatch = id.match(/\d+/);
                const num = numMatch ? numMatch[0] : '';
                if (!num) return;
                setSelectedOrbit(`Orbit ${num}`);
                return;
            }

            const firstId = visiblePoints[0].id;
            const lastId = visiblePoints[visiblePoints.length - 1].id;
            const firstMatch = firstId.match(/\d+/);
            const lastMatch = lastId.match(/\d+/);
            if (!firstMatch || !lastMatch) return;
            const firstNum = firstMatch[0];
            const lastNum = lastMatch[0];
            // Range display: Orbit X-Y
            setSelectedOrbit(`Orbit ${firstNum}-${lastNum}`);
        }
    });

    useScrollBoundary({
        scrollAreaRef: scrollAreaRef,
        direction: "horizontal",
        onBoundaryChange: (boundary) => {
            if (!scenario || !operateMode || !boundary) return
            const time = boundary === 'start' ? simulation_start_time : simulation_end_time
            getUpdatedSimulationTime(scenario, time, current_time, simulation_duration)
        },
        onVerticalScroll: (scroll) => {
            setSelectedFrame((prev) => {
                // map current label -> minutes -> next minutes -> label
                const currentMinutes = TimeFrameInMinutes[prev as keyof typeof TimeFrameInMinutes] ?? TimeFrameInMinutes[TimeFrameOptions[0]]
                const nextMinutes = getNextTimeFrame(currentMinutes, scroll)
                const nextOption = TimeFrameOptions.find(option => TimeFrameInMinutes[option as keyof typeof TimeFrameInMinutes] === nextMinutes)
                return nextOption ?? prev
            })
        }
    });

    const onSelectFrame = (frame: string) => {
        const value = TimeFrameInMinutes[frame]
        setSelectedFrame(frame)
        if ([30, 60].includes(value) && selectedOrbit.includes('-') && scrollAreaRef.current) {
            scrollAreaRef.current.scrollLeft = 0
            setSelectedOrbit('Orbit 1')
        }
    }

    const configuredGroundStations = useMemo(() => {
        return operateMode ? Array.from(new Map(configured_gs.map(station => [station?.['antennaId'] || station?.['groundStationName'], station])).values()) : configured_gs
    }, [configured_gs, operateMode])

    return (
        <div className='ground__container__left__container'>
            {showFrameButton &&
                <button className={`update__plan__frame__button  
                    ${showFrameButton === 'start' ? 'left' : 'right'}`}
                    onClick={() => getUpdatedFrame(showFrameButton)}
                >
                    <CustomToolTip placement={'top'} title={showFrameButton === 'start' ? 'Jump to previous' : 'Jump to next'}>
                        <ButtonSvg />
                    </CustomToolTip>
                </button>
            }

            <Timeline
                scrollAreaRef={scrollAreaRef}
                timelineDuration={frameDuration}
                timelineStartTime={frameStartTime}
                timelineEndTime={frameEndTime}
                selectedFrame={TimeFrameInMinutes[selectedFrame]}
                loading={loading}
                sidebarSize={150}
                hideToggleButton={true}
                className='timeline__background'
            >
                <div className='timeline__sidebar__mask' />
                <TimelineHeader className='plan__timeline__header__container'>
                    <div className='hud__timeline__header'>
                        <div className='hud__timeline__header__left__container'>
                            <span className='hud__timeline__header__left__text'>Ground Stations</span>
                        </div>
                        <div className='hud__timeline__header__right__container'>
                            <div className='hud__timeline__header__timeDetails__container plan__view'>
                                <div className='hud__timeline__header__time__label'>
                                    <CustomToolTip title={operateMode ? 'Current UTC Time' : 'Simulation Start Time'}>
                                        {operateMode ? 'Current UTC' : 'Start'}
                                    </CustomToolTip>
                                </div>
                                <div className='hud__timeline__header__time__details'>{getFormatedTime(time)}</div>
                            </div>
                            <LiveButton onClick={go_live} />
                            <div className='hud__timeframe__details__container'>
                                <div className='timeframe__label'>
                                    <CustomToolTip title='Timeframe'>
                                        <TimeIntervalSvg />
                                    </CustomToolTip>
                                </div>
                                <div className='hud__omni__select__container'>
                                    <OmniSelect
                                        placement='bottom'
                                        id='omni__select__timeframe'
                                        open={openSelect}
                                        setOpen={setOpenSelect}
                                        disabled={false}
                                        className='updated__omni__select__border'
                                        options={TimeFrameOptions}
                                        value={selectedFrame}
                                        onChange={onSelectFrame}
                                        headerOption={{
                                            title: 'Timeframe',
                                            icon: <TimeIntervalSvg />
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </TimelineHeader>
                <TimelineBodyContainer>
                    <TimelineScrollContainer>
                        <TimelineContainer>
                            <TimelineTrackContainer>
                                <TimelineTrack className='timeline__background' />
                                <RealtimeTracker
                                    strtTime={simulation_start_time}
                                    endTime={simulation_end_time}
                                    duration={simulation_duration}
                                    onChange={change_viewer_time}
                                    currentTime={current_time}
                                    disabled={dashboard !== 'summary'}
                                />
                            </TimelineTrackContainer>
                        </TimelineContainer>
                        {configuredGroundStations?.map(station => {
                            const stationId = station.groundStationId
                            const stationName = station.groundStationName
                            return (
                                <Fragment key={stationId}>
                                    <TimelineContainer
                                        sidebarDetails={{
                                            name: stationName,
                                            className: 'timeline__sidebar__container'
                                        }}
                                    >
                                        <TimelineEventTrack>
                                            {planingContactData?.['contacts']?.[stationId] && planingContactData?.['contacts']?.[stationId].map((event: ContactStation) => {
                                                const { startTime, endTime } = event
                                                const isNotBooked = event?.status === 'NotBooked'
                                                const icon = <ContactTaskSvg />
                                                const selected = selectedContact?.contact_data?.uniqueId === event?.uniqueId ? 'selected_card' : ''
                                                const status = getContactCurrentStatus(current_time, startTime, endTime, isNotBooked)
                                                const statusIcon = isNotBooked ? null : event_status_state[status]?.svg
                                                const uniqueid = `event-${stationId}-${startTime}-${endTime}-${stationName}`
                                                if (event?.status === 'Conflict') return null
                                                return (
                                                    <Event
                                                        key={uniqueid}
                                                        icon={icon}
                                                        statusIcon={statusIcon}
                                                        endTime={endTime}
                                                        eventName={stationName}
                                                        startTime={startTime}
                                                        allEvents={planingContactData?.['contacts']?.[stationId]}
                                                        eventType={status}
                                                        className={selected}
                                                        overlay={ContactOverlay}
                                                        onClick={() => {
                                                            setSelectedContact({
                                                                selection_type: 'manual',
                                                                contact_type: status === 'ongoing' ? 'Ongoing' : status === 'upcoming' ? 'Upcoming' : 'Previous',
                                                                contact_data: event,
                                                            })
                                                        }}
                                                    />
                                                )
                                            })}
                                        </TimelineEventTrack>

                                    </TimelineContainer>
                                    <TimelineContainerDevider />
                                </Fragment>
                            )
                        })}
                    </TimelineScrollContainer>
                </TimelineBodyContainer>
            </Timeline>
        </div>
    )
}

export default GroundContactSection